import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useSEO } from '../../../hooks/useSEO';
import { useFilamentText } from '../../../hooks/useFilamentText';
import { useLocation } from 'react-router-dom';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const PLAPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = useFilamentText();

  useSEO({
    title: 'PLA',
    description: isEnglish 
      ? 'The most user-friendly beginner filament. Low-temperature printing, minimal warping, easy to print. Detailed explanation of recommended settings, features, and derivatives (PLA+, PLA-CF, etc.).'
      : '最も使いやすい初心者向けフィラメント。低温で印刷可能、反りが少なく造形が容易。推奨設定、特徴、派生材料（PLA+、PLA-CF等）を詳しく解説。',
    keywords: '3Dプリンター,PLA,フィラメント,初心者,設定,温度,ポリ乳酸',
    canonicalPath: isEnglish ? '/en/filament/pla' : '/filament/pla'
  });

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'PLA', path: '/en/filament/pla' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: 'PLA', path: '/filament/pla' }
  ];

  const filamentId = 'pla';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                PLA
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="pla" />

            {/* 特徴（最上部に大きく配置） */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Low-temperature printing, minimal warping' : '低温で印刷可能、反りが少ない'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Biodegradable plastic' : '生分解性プラスチック'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Ideal for beginners' : '初心者に最適'}</span>
                  </li>
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Wide color variety' : '豊富なカラーバリエーション'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Good bed adhesion without glue' : '接着剤不要でベッドに密着'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Odorless and safe' : '無臭で安全'}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-beginner px-3 py-1 rounded text-sm font-semibold">{TEXT.beginner}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{isEnglish ? 'Polylactic Acid' : 'ポリ乳酸'}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.meltingPoint}</span>
                      <span className="text-white">150-160°C</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.density}</span>
                      <span className="text-white">1.24 g/cm³</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.shrinkageRate}</span>
                      <span className="text-white">0.3-0.5%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.heatResistance}</span>
                      <span className="text-white">50-60°C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">190-220°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Adjust by brand' : 'ブランドにより調整'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">50-60°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'For glass bed' : 'ガラスベッドの場合'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">100%</td>
                      <td className="px-4 py-3">{isEnglish ? 'From layer 2' : '2層目から'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">40-60mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? '40mm/s for quality' : '品質重視なら40mm/s'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Standard setting' : '標準設定'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Sensitive to moisture, store in sealed container' : '湿気に弱い、密閉保管推奨'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            {/* 応力-ひずみグラフ（CSV版） */}
            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="PLA"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.derivatives}
              </h2>
              <p className="text-metal-light mb-6">
                {isEnglish 
                  ? 'Various derivative filaments with enhanced properties have been developed based on PLA. Choose the optimal one according to your application.'
                  : 'PLAをベースに、様々な特性を強化した派生フィラメントが開発されています。用途に応じて最適なものを選択してください。'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <Link 
                  to={isEnglish ? "/en/filament/pla-plus" : "/filament/pla-plus"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PLA+</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Enhanced impact resistance. Improved mechanical strength.' : '耐衝撃性を強化したPLA。機械的強度が向上。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/matte-pla" : "/filament/matte-pla"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">Matte PLA</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Matte finish. Layer lines less visible, premium look.' : 'マット仕上げ。積層痕が目立ちにくい高級感。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/pla-silk" : "/filament/pla-silk"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">Silk PLA</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Glossy finish. Beautiful appearance. For decorative items.' : '光沢重視。美しい仕上がりが特徴。装飾品向け。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/pla-cf" : "/filament/pla-cf"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PLA-CF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Carbon fiber reinforced. Lightweight and strong. Hardened nozzle recommended.' : 'カーボン繊維入り。軽量高強度。硬化ノズル推奨。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/pla-pro" : "/filament/pla-pro"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PLA PRO</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'High strength and toughness. Suitable for functional parts.' : '高強度・高靭性。機能部品にも使用可能。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/ht-pla" : "/filament/ht-pla"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">HT-PLA</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'High heat resistance PLA. Over 100°C with annealing.' : '高耐熱PLA。アニーリングで100°C以上に対応。'}</p>
                </Link>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '適した用途'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    <li>• {isEnglish ? 'Prototyping' : 'プロトタイプ製作'}</li>
                    <li>• {isEnglish ? 'Decorative items & figurines' : '装飾品・フィギュア'}</li>
                    <li>• {isEnglish ? 'Educational models' : '教育用モデル'}</li>
                    <li>• {isEnglish ? 'Complex shape prototypes' : '複雑な形状の試作'}</li>
                    <li>• {isEnglish ? 'Architectural models' : '建築模型'}</li>
                    <li>• {isEnglish ? 'Toys & gadgets' : 'おもちゃ・ガジェット'}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : 'メリット・デメリット'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : 'メリット:'}</span>
                      <span className="text-metal-light"> {isEnglish ? 'Easy to use, affordable, wide color range, odorless' : '扱いやすい、安価、豊富な色、無臭'}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : 'デメリット:'}</span>
                      <span className="text-metal-light"> {isEnglish ? 'Low heat resistance, brittle, UV degradation' : '耐熱性低い、脆い、UV劣化'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                <p>• {isEnglish ? 'Absorbs moisture easily, store in sealed container recommended.' : '湿気を吸収しやすいため、密閉容器での保管を推奨します。'}</p>
                <p>• {isEnglish ? 'May deform in environments above 60°C.' : '60°C以上の環境では変形する可能性があります。'}</p>
                <p>• {isEnglish ? 'Weak layer adhesion, not suitable for parts requiring mechanical strength.' : '層間接着が弱いため、機械的強度が必要な部品には不向きです。'}</p>
                <p>• {isEnglish ? 'Degrades under direct sunlight, avoid outdoor use.' : '直射日光により劣化するため、屋外使用は避けてください。'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PLAPage;
